import { useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import queryString from 'query-string'

import type { CommentTopics } from 'services/content/types'
import Comment from './Comment'
import CommentForm from './CommentForm'
import Header from './Header'
import FilterBar from './FilterBar'
import useComments from 'hooks/useComments'
import Loader from 'content-components/_Primatives/Loader'
import {
  Wrapper,
  Comments,
} from 'ui-component-library/components/CommentsModule'
import { getCommentStrings } from 'store/strings/selectors'

type Props = {
  topics: CommentTopics
}

function CommentsModuleContainer({ topics }: Props) {
  const commentsRef = useRef<HTMLDivElement>(null)
  const [showCommentPostForm, setShowCommentPostForm] = useState(false)
  const location = useLocation()
  const { formText } = useSelector(getCommentStrings)

  const selectedTopic = useMemo(() => {
    const { topicId } = queryString.parse(location.search)
    return topics.find((topic) => topic.topicId === topicId)
  }, [location, topics])

  const {
    count,
    comments,
    isLoading,
    activeSort,
    onSort,
    onSubmitPost,
    onSubmitReply,
    onSubmitEdit,
  } = useComments({
    topicIds: topics.map(({ topicId }) => topicId),
    selectedTopicId: selectedTopic && selectedTopic.topicId,
  })

  function handleShowCommentForm() {
    setShowCommentPostForm(true)
  }

  function handleCloseForm() {
    setShowCommentPostForm(false)
  }

  function handleSubmitPost(text: string) {
    return onSubmitPost({
      text,
      id: selectedTopic ? selectedTopic.topicId : topics[0].topicId,
    })
  }

  return (
    <Wrapper>
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <Header
            topics={topics}
            selectedTopic={selectedTopic}
            commentsCount={count}
            commentsRef={commentsRef}
            onShowCommentForm={handleShowCommentForm}
          />

          <FilterBar
            onSort={onSort}
            activeSort={activeSort}
            selectedTopic={selectedTopic}
            topics={topics}
          />

          <Comments ref={commentsRef}>
            {comments.map((comment) => (
              <Comment
                key={comment.id}
                comment={comment}
                onSubmitReply={onSubmitReply}
                onSubmitEdit={onSubmitEdit}
                topic={topics.find(
                  (topic) => topic.topicId === comment.topicId,
                )}
              >
                {comment.replies.map((reply) => (
                  <Comment
                    key={reply.id}
                    onSubmitEdit={onSubmitEdit}
                    comment={reply}
                    topic={topics.find(
                      (topic) => topic.topicId === reply.topicId,
                    )}
                  />
                ))}
              </Comment>
            ))}
          </Comments>

          {showCommentPostForm && (
            <CommentForm
              buttonText={formText.post.buttonText}
              placeholderText={formText.post.placeholderText}
              onClose={handleCloseForm}
              onSubmit={handleSubmitPost}
            />
          )}
        </>
      )}
    </Wrapper>
  )
}

export default CommentsModuleContainer
